/**
 * What the bare domain (bcportal.app) renders instead of the tenant app: the landing page, or a
 * legal document at /legal/{slug} (so the privacy policy and terms have a platform-level address).
 * Not wrapped in TenantProvider, so no tenant is looked up, and no session is used.
 */
import { useEffect } from 'react';
import RootLandingPage from './pages/RootLandingPage';
import LegalDocument from './pages/LegalDocument';

/** Follow a saved theme, else the device's light/dark setting (nothing is written). */
function useRootTheme(): void {
  useEffect(() => {
    const root = document.documentElement;
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    const apply = () => {
      let saved: string | null = null;
      try { saved = localStorage.getItem('bc-portal-theme'); } catch { /* storage blocked */ }
      const dark = saved ? saved === 'dark' : !!mq?.matches;
      root.classList.remove('light-theme', 'dark-theme', 'dark');
      root.classList.add(dark ? 'dark-theme' : 'light-theme');
      if (dark) root.classList.add('dark');
    };
    apply();
    mq?.addEventListener?.('change', apply);
    return () => mq?.removeEventListener?.('change', apply);
  }, []);
}

const BASE_TITLE = (typeof document !== 'undefined' && document.title) || 'Goodstuff Portal';

export function legalSlugFromRootPath(pathname: string): string | null {
  const m = /^\/legal\/([a-z0-9-]{1,64})\/?$/.exec(pathname);
  return m ? m[1] : null;
}

export default function RootSite({ pathname = window.location.pathname }: { pathname?: string }) {
  useRootTheme();
  const slug = legalSlugFromRootPath(pathname);
  useEffect(() => {
    document.title = `${slug ? 'Legal' : 'Sign in to your portal'} – ${BASE_TITLE}`;
  }, [slug]);
  if (slug) {
    return (
      <main id="main-content">
        <LegalDocument slug={slug} onBack={() => window.location.assign('/')} />
      </main>
    );
  }
  return <RootLandingPage />;
}
